import React from 'react';
import { Link } from 'react-router-dom';
import { HandHeart, MagnifyingGlass, CaretRight, Car, Bed, Barbell } from '@phosphor-icons/react';

// Renders Option B (Aurora Borealis) and Option E (Neo-Mint Pastel) backgrounds
// side-by-side with representative UI components from the live app, so the user
// can pick a vibrant theme without committing global CSS changes first.

const SampleHeroCard = ({ accentClass }) => (
  <div className="relative max-w-md mx-auto mt-4 mb-3 px-4 sm:px-0">
    <div className="bg-white rounded-2xl border border-slate-200 p-4 shadow-xl">
      <p className="text-[10px] uppercase tracking-wider text-slate-400 font-semibold mb-1">Location</p>
      <div className="flex items-center gap-2 mb-3">
        <MagnifyingGlass size={16} className="text-slate-400" />
        <span className="text-sm text-slate-500">City, area or pincode...</span>
      </div>
      <button className={`w-full ${accentClass} text-white text-sm font-bold py-2.5 rounded-xl shadow`}>Search</button>
    </div>
  </div>
);

const SampleLiveStrip = () => (
  <div className="relative max-w-md mx-auto px-4 sm:px-0 mb-4">
    <div className="bg-gradient-to-r from-pink-500 via-fuchsia-500 to-purple-600 rounded-xl px-3 py-2.5 flex items-center justify-between shadow-lg">
      <div className="flex items-center gap-2">
        <span className="w-6 h-6 rounded-lg bg-white/20 ring-1 ring-white/40 flex items-center justify-center">
          <HandHeart size={12} weight="fill" className="text-white" />
        </span>
        <span className="text-white font-bold text-xs">Live Sessions</span>
      </div>
      <span className="bg-white text-pink-600 rounded-full px-2 py-0.5 text-[10px] font-bold flex items-center gap-1">
        Browse <CaretRight size={10} weight="bold" />
      </span>
    </div>
  </div>
);

const SampleCategoryTiles = () => {
  const tiles = [
    { icon: Car, name: 'Parking', bg: 'bg-blue-100', color: 'text-blue-600' },
    { icon: Bed, name: 'Hotels', bg: 'bg-amber-100', color: 'text-amber-700' },
    { icon: Barbell, name: 'Gyms', bg: 'bg-emerald-100', color: 'text-emerald-700' },
  ];
  return (
    <div className="relative max-w-md mx-auto px-4 sm:px-0">
      <div className="grid grid-cols-3 gap-3">
        {tiles.map(t => {
          const Icon = t.icon;
          return (
            <div key={t.name} className="bg-white rounded-xl p-3 flex flex-col items-center shadow border border-white/70">
              <div className={`w-10 h-10 ${t.bg} rounded-lg flex items-center justify-center mb-1.5`}>
                <Icon size={20} className={t.color} weight="bold" />
              </div>
              <span className="text-xs font-semibold text-slate-700">{t.name}</span>
            </div>
          );
        })}
      </div>
    </div>
  );
};

const PanelHeader = ({ title, subtitle, swatches }) => (
  <div className="relative max-w-md mx-auto pt-6 pb-4 px-4 sm:px-0 text-white">
    <h2 className="text-2xl font-extrabold tracking-tight drop-shadow">{title}</h2>
    <p className="text-sm opacity-90 mt-1 mb-3 drop-shadow">{subtitle}</p>
    <div className="flex items-center gap-2">
      {swatches.map((c, i) => (
        <span key={`sw-${i}`} className="w-6 h-6 rounded-full ring-2 ring-white/40 shadow-md" style={{ background: c }} />
      ))}
    </div>
  </div>
);

const AuroraPanel = () => (
  <div className="relative flex-1 min-h-screen overflow-hidden" data-testid="preview-panel-aurora" style={{ background: '#0F0B2E' }}>
    {/* Animated blobs */}
    <div className="absolute inset-0 pointer-events-none">
      <div className="absolute w-[600px] h-[600px] rounded-full opacity-60 blob-aurora-1" style={{ background: 'radial-gradient(circle, rgba(6,182,212,0.7), transparent 60%)', top: '-100px', left: '-150px', filter: 'blur(40px)' }} />
      <div className="absolute w-[700px] h-[700px] rounded-full opacity-60 blob-aurora-2" style={{ background: 'radial-gradient(circle, rgba(168,85,247,0.7), transparent 60%)', top: '20%', right: '-200px', filter: 'blur(40px)' }} />
      <div className="absolute w-[650px] h-[650px] rounded-full opacity-50 blob-aurora-3" style={{ background: 'radial-gradient(circle, rgba(52,211,153,0.65), transparent 60%)', bottom: '-100px', left: '20%', filter: 'blur(40px)' }} />
      <div className="absolute w-[500px] h-[500px] rounded-full opacity-40 blob-aurora-4" style={{ background: 'radial-gradient(circle, rgba(236,72,153,0.6), transparent 60%)', bottom: '20%', right: '10%', filter: 'blur(40px)' }} />
    </div>
    <style>{`
      @keyframes blobA1 { 0%,100%{transform:translate(0,0) scale(1)} 50%{transform:translate(80px,60px) scale(1.1)} }
      @keyframes blobA2 { 0%,100%{transform:translate(0,0) scale(1)} 50%{transform:translate(-100px,80px) scale(1.15)} }
      @keyframes blobA3 { 0%,100%{transform:translate(0,0) scale(1)} 50%{transform:translate(60px,-70px) scale(1.1)} }
      @keyframes blobA4 { 0%,100%{transform:translate(0,0) scale(1)} 50%{transform:translate(-50px,-50px) scale(1.05)} }
      .blob-aurora-1{animation:blobA1 16s ease-in-out infinite}
      .blob-aurora-2{animation:blobA2 22s ease-in-out infinite}
      .blob-aurora-3{animation:blobA3 18s ease-in-out infinite}
      .blob-aurora-4{animation:blobA4 26s ease-in-out infinite}
    `}</style>
    <PanelHeader title="Option B — Aurora Borealis" subtitle="Dark base + 4 animated colour blobs · Tech-forward · Premium feel" swatches={['#0F0B2E', '#06B6D4', '#A855F7', '#34D399']} />
    <SampleHeroCard accentClass="bg-cyan-500 hover:bg-cyan-600" />
    <SampleLiveStrip />
    <SampleCategoryTiles />
  </div>
);

const PastelPanel = () => (
  <div className="relative flex-1 min-h-screen overflow-hidden" data-testid="preview-panel-pastel"
       style={{
         background: `radial-gradient(ellipse 100% 50% at 0% 0%, #C4B5FD 0%, transparent 50%),
                      radial-gradient(ellipse 80% 60% at 100% 30%, #FDE68A 0%, transparent 55%),
                      radial-gradient(ellipse 90% 60% at 50% 100%, #86EFAC 0%, transparent 50%),
                      linear-gradient(180deg, #FEFCE8 0%, #ECFDF5 100%)`
       }}>
    <div className="relative max-w-md mx-auto pt-6 pb-4 px-4 sm:px-0 text-slate-800">
      <h2 className="text-2xl font-extrabold tracking-tight">Option E — Neo-Mint Pastel</h2>
      <p className="text-sm opacity-80 mt-1 mb-3">Soft 4-stop gradient · Calm · Long-session friendly</p>
      <div className="flex items-center gap-2">
        {['#C4B5FD', '#FDE68A', '#86EFAC', '#FEFCE8'].map(c => (
          <span key={c} className="w-6 h-6 rounded-full ring-2 ring-white/70 shadow-md" style={{ background: c }} />
        ))}
      </div>
    </div>
    <SampleHeroCard accentClass="bg-emerald-500 hover:bg-emerald-600" />
    <SampleLiveStrip />
    <SampleCategoryTiles />
  </div>
);

const BackgroundsPreview = () => (
  <div className="min-h-screen w-full" data-testid="backgrounds-preview-page">
    {/* Top bar */}
    <div className="sticky top-0 z-50 bg-slate-900 text-white px-4 py-2.5 flex items-center justify-between shadow-md">
      <div className="flex items-center gap-3">
        <span className="text-sm font-bold">Background Preview</span>
        <span className="text-xs opacity-70 hidden sm:inline">Live components on both candidate themes — pick a side and reply with the letter</span>
      </div>
      <Link to="/buyer" className="text-xs bg-white text-slate-900 px-3 py-1 rounded-full font-semibold hover:bg-slate-100">Back to app</Link>
    </div>
    {/* Two panels */}
    <div className="flex flex-col md:flex-row">
      <AuroraPanel />
      <PastelPanel />
    </div>
    <div className="px-4 py-4 bg-slate-50 text-center text-xs text-slate-500">
      Tip: Resize your window — both layouts adapt. Aurora's blobs animate slowly (16-26s loops, ~1% CPU).
    </div>
  </div>
);

export default BackgroundsPreview;
